import { deepMerge } from "./utils/deepmerge";
import { defaultSuggesterConfig } from "./config/default-suggester-config";
export default class TPSAzureSuggester {
    constructor(inputNode, config) {
        this.initialized = false;
        this.config = deepMerge(defaultSuggesterConfig, config);
        this.inputNode = inputNode;
        this.inputHasVisualFocus = false;
        this.listboxHasVisualFocus = false;
        this.hasHover = false;
        this.option = null;
        this.firstOption = null;
        this.lastOption = null;
        this.filter = '';
        this.filteredOptions = [];
    }
    init() {
        this.populateConfigFromInputData();
        this.setupAutocomplete();
    }
    populateConfigFromInputData() {
        if (this.inputNode) {
            const inputData = this.inputNode.dataset;
            // Select suggster
            if (inputData['azureSuggester'])
                this.config.searchParams.suggesterName = inputData['azureSuggester'];
            // API Key
            if (inputData['azureSuggesterKey'])
                this.config.azureSearch.key = inputData['azureSuggesterKey'];
            // Construct API Url
            if (inputData['azureSuggesterEndpoint'] && inputData['azureSuggesterType'] && inputData['azureSuggesterApiVersion'])
                this.config.azureSearch.url = inputData['azureSuggesterEndpoint'] + inputData['azureSuggesterType'] + '?api-version=' + inputData['azureSuggesterApiVersion'];
            // apply and odata filter to the suggester
            if (inputData['azureSuggesterFilter'])
                this.config.searchParams.filter = inputData['azureSuggesterFilter'];
            // Select fields from the suggester that will be searched (needs to be defined on suggester)
            if (inputData['azureSuggesterFields'])
                this.config.searchParams.searchFields = inputData['azureSuggesterFields'];
            // Select what index fields should be returned in the results set
            if (inputData['azureSuggesterSelect'])
                this.config.searchParams.select = inputData['azureSuggesterSelect'];
            // Select what index fields should be returned in the results set
            if (inputData['azureSuggesterCount'])
                this.config.searchParams.top = Number(inputData['azureSuggesterCount']);
            // Use fuzzy search
            if (inputData['azureSuggesterFuzzy'])
                this.config.searchParams.fuzzy = inputData['azureSuggesterFuzzy'] === 'true';
            // API Key
            if (inputData['azureSuggesterOrderby'])
                this.config.searchParams.orderby = inputData['azureSuggesterOrderby'];
            if (inputData['azureSuggesterMinCharLength'])
                this.config.suggestions.minLength = Number(inputData['azureSuggesterMinCharLength']);
        }
        else {
            this.debug('you have not provided an input field.+');
        }
    }
    setupAutocomplete() {
        if (this.inputNode) {
            // Listbox
            const listbox = document.createElement('ul');
            const listboxID = this.inputNode.id ? `${this.inputNode.id}-listbox` : `listbox-${this.generateRandomId()}`;
            listbox.role = 'listbox';
            listbox.id = listboxID;
            if (this.config.suggestions.listBoxAriaLabel) {
                listbox.setAttribute('aria-label', this.config.suggestions.listBoxAriaLabel);
            }
            this.listboxNode = listbox;
            // Add input attributes for accessibility.
            this.inputNode.role = 'combobox';
            this.inputNode.setAttribute('aria-autocomplete', 'list');
            this.inputNode.setAttribute('aria-expanded', 'false');
            this.inputNode.setAttribute('aria-controls', listboxID);
            // Wrapper
            const wrapper = document.createElement('div');
            wrapper.className = 'tps-autocomplete';
            this.wrap(this.inputNode, wrapper);
            // Loading icon
            const loadingIcon = document.createElement('span');
            loadingIcon.className = 'tps-autocomplete-loading-icon';
            loadingIcon.ariaHidden = 'true';
            // Append the icon and the listbox
            wrapper.append(loadingIcon);
            wrapper.append(listbox);
            // Add listeners
            this.inputNode.addEventListener('keydown', this.onInputKeyDown.bind(this));
            this.inputNode.addEventListener('keyup', this.onInputKeyUp.bind(this));
            this.inputNode.addEventListener('focus', this.onInputFocus.bind(this));
            this.inputNode.addEventListener('blur', this.onInputBlur.bind(this));
            document.body.addEventListener('pointerup', this.onBackgroundPointerUp.bind(this), true);
            this.listboxNode.addEventListener('pointerover', this.onListboxPointerover.bind(this));
            this.listboxNode.addEventListener('pointerout', this.onListboxPointerout.bind(this));
            this.listboxNode.addEventListener('click', this.onListboxClick.bind(this));
        }
    }
    maybeGetElementFromConfig(selectorOrElement) {
        if (selectorOrElement instanceof Element) {
            return selectorOrElement;
        }
        else if (typeof selectorOrElement === 'string' && selectorOrElement !== '') {
            return document.querySelector(selectorOrElement);
        }
        else {
            return null;
        }
    }
    debug(message) {
        if (this.config.debug) {
            console.log(message);
        }
    }
    wrap(element, wrapper) {
        var _a;
        (_a = element.parentNode) === null || _a === void 0 ? void 0 : _a.insertBefore(wrapper, element);
        wrapper.appendChild(element);
    }
    generateRandomId() {
        return Math.floor(Math.random() * Date.now());
    }
    setActiveDescendant(option) {
        if (option && this.listboxHasVisualFocus) {
            this.inputNode.setAttribute('aria-activedescendant', option.id);
        }
        else {
            this.inputNode.setAttribute('aria-activedescendant', '');
        }
    }
    setValue(value) {
        this.filter = value;
        this.inputNode.value = this.filter;
        this.inputNode.setSelectionRange(this.filter.length, this.filter.length);
        this.filterOptions();
    }
    setOption(option) {
        if (option) {
            this.option = option;
            this.setCurrentOptionStyle(this.option);
            this.setActiveDescendant(this.option);
        }
    }
    setVisualFocusInput() {
        var _a, _b;
        (_a = this.listboxNode) === null || _a === void 0 ? void 0 : _a.classList.remove('visual-focus');
        (_b = this.inputNode) === null || _b === void 0 ? void 0 : _b.classList.add('visual-focus');
        this.inputHasVisualFocus = true;
        this.listboxHasVisualFocus = false;
        this.setActiveDescendant(false);
    }
    setVisualFocusListbox() {
        var _a, _b;
        (_a = this.inputNode) === null || _a === void 0 ? void 0 : _a.classList.remove('visual-focus');
        this.inputHasVisualFocus = false;
        (_b = this.listboxNode) === null || _b === void 0 ? void 0 : _b.classList.add('visual-focus');
        this.listboxHasVisualFocus = true;
    }
    removeVisualFocusAll() {
        var _a, _b;
        (_a = this.inputNode) === null || _a === void 0 ? void 0 : _a.classList.remove('visual-focus');
        (_b = this.listboxNode) === null || _b === void 0 ? void 0 : _b.classList.remove('visual-focus');
        this.inputHasVisualFocus = false;
        this.listboxHasVisualFocus = false;
        this.setActiveDescendant(false);
    }
    filterOptions() {
        if (this.filter.length >= this.config.suggestions.minLength) {
            this.config.searchParams.search = this.filter;
            fetch(this.config.azureSearch.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'api-key': this.config.azureSearch.key,
                    'Cache-Control': 'no-cache'
                },
                body: JSON.stringify(this.config.searchParams)
            })
                .then(response => response.json())
                .then(result => {
                this.filteredOptions = [];
                this.listboxNode.innerHTML = '';
                if (result.value) {
                    result.value.forEach(item => {
                        const listItem = this.createListItemElement(this.filter, item);
                        this.filteredOptions.push(listItem);
                        this.listboxNode.append(listItem);
                    });
                    // Use populated options array to initialize firstOption and lastOption.
                    if (this.filteredOptions.length > 0) {
                        this.firstOption = this.filteredOptions[0];
                        this.lastOption = this.filteredOptions[this.filteredOptions.length - 1];
                    }
                    else {
                        this.firstOption = null;
                        this.lastOption = null;
                    }
                }
                this.open();
                // Call user-defined function on results.
                this.config.onResults(result, this);
            })
                .catch(error => {
                console.error(error);
            });
        }
    }
    createListItemElement(searchTerm, item) {
        const listElement = document.createElement('li');
        listElement.id = `option-${this.generateRandomId()}`;
        listElement.role = 'option';
        listElement.dataset.url = item['url'];
        listElement.innerHTML = item['@search.text'];
        return listElement;
    }
    setCurrentOptionStyle(option) {
        this.filteredOptions.forEach(filteredOption => {
            if (option === filteredOption) {
                filteredOption.setAttribute('aria-selected', 'true');
                if (this.listboxNode.scrollTop + this.listboxNode.offsetHeight < filteredOption.offsetTop + filteredOption.offsetHeight) {
                    this.listboxNode.scrollTop = filteredOption.offsetTop + filteredOption.offsetHeight - this.listboxNode.offsetHeight;
                }
                else if (this.listboxNode.scrollTop > filteredOption.offsetTop + 2) {
                    this.listboxNode.scrollTop = filteredOption.offsetTop;
                }
            }
            else {
                filteredOption.removeAttribute('aria-selected');
            }
        });
    }
    getPreviousOption(currentOption) {
        if (currentOption !== this.firstOption) {
            const index = this.filteredOptions.indexOf(currentOption);
            return this.filteredOptions[index - 1];
        }
        return this.lastOption;
    }
    getNextOption(currentOption) {
        if (currentOption !== this.lastOption) {
            const index = this.filteredOptions.indexOf(currentOption);
            return this.filteredOptions[index + 1];
        }
        return this.firstOption;
    }
    doesOptionHaveFocus() {
        return this.inputNode.getAttribute('aria-activedescendant') !== '';
    }
    isOpen() {
        return this.listboxNode.classList.contains('active');
    }
    isClosed() {
        return !this.listboxNode.classList.contains('active');
    }
    hasOptions() {
        return this.filteredOptions.length;
    }
    open() {
        this.listboxNode.classList.add('active');
        this.inputNode.setAttribute('aria-expanded', 'true');
    }
    close(force = false) {
        if (force || (!this.inputHasVisualFocus && !this.listboxHasVisualFocus && !this.hasHover)) {
            this.setCurrentOptionStyle(null);
            this.listboxNode.classList.remove('active');
            this.inputNode.setAttribute('aria-expanded', 'false');
            this.setActiveDescendant(false);
            this.inputNode.classList.add('visual-focus');
        }
    }
    onInputKeyDown(event) {
        let flag = false;
        const altKey = event.altKey;
        if (event.ctrlKey || event.shiftKey)
            return;
        switch (event.key) {
            case 'Enter':
                if (this.listboxHasVisualFocus) {
                    this.setValue(this.option.textContent);
                }
                this.close(true);
                this.setVisualFocusInput();
                if (this.config.suggestions.onSelect) {
                    this.config.suggestions.onSelect(event, this.option, this.inputNode.value);
                }
                flag = true;
                break;
            case 'Down':
            case 'ArrowDown':
                if (this.filteredOptions.length > 0) {
                    if (altKey) {
                        this.open();
                    }
                    else {
                        this.open();
                        if (this.listboxHasVisualFocus) {
                            this.setOption(this.getNextOption(this.option));
                            this.setVisualFocusListbox();
                        }
                        else {
                            this.setOption(this.firstOption);
                            this.setVisualFocusListbox();
                        }
                    }
                }
                flag = true;
                break;
            case 'Up':
            case 'ArrowUp':
                if (this.hasOptions()) {
                    if (this.listboxHasVisualFocus) {
                        this.setOption(this.getPreviousOption(this.option));
                    }
                    else {
                        this.open();
                        if (!altKey) {
                            this.setOption(this.lastOption);
                            this.setVisualFocusListbox();
                        }
                    }
                }
                flag = true;
                break;
            case 'Esc':
            case 'Escape':
                if (this.isOpen()) {
                    this.close(true);
                    this.filter = this.inputNode.value;
                    this.filterOptions();
                    this.setVisualFocusListbox();
                }
                else {
                    this.setValue('');
                    this.inputNode.value = '';
                }
                this.option = null;
                flag = true;
                break;
            case 'Tab':
                this.close(true);
                if (this.listboxHasVisualFocus && this.option) {
                    this.setValue(this.option.textContent);
                }
                break;
            case 'Home':
                this.inputNode.setSelectionRange(0, 0);
                flag = true;
                break;
            case 'End':
                this.inputNode.setSelectionRange(this.inputNode.value.length, this.inputNode.value.length);
                flag = true;
                break;
            default:
                break;
        }
        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    isPrintableCharacter(str) {
        return str.length === 1 && str.match(/\S| /);
    }
    onInputKeyUp(event) {
        let flag = false;
        let char = event.key;
        if (this.isPrintableCharacter(char)) {
            this.filter += char;
        }
        // this is for the case when a selection in the textbox has been deleted
        if (this.inputNode.value.length < this.filter.length) {
            this.filter = this.inputNode.value;
            this.option = null;
            this.filterOptions();
        }
        if (event.key === 'Escape' || event.key === 'Esc') {
            return;
        }
        switch (event.key) {
            case 'Backspace':
                this.setVisualFocusInput();
                this.setCurrentOptionStyle(null);
                this.filter = this.inputNode.value;
                this.option = null;
                this.filterOptions();
                flag = true;
                break;
            case 'Left':
            case 'ArrowLeft':
            case 'Right':
            case 'ArrowRight':
            case 'Home':
            case 'End':
                this.option = null;
                this.setCurrentOptionStyle(null);
                this.setVisualFocusInput();
                flag = true;
                break;
            default:
                if (this.isPrintableCharacter(char)) {
                    this.setVisualFocusInput();
                    this.setCurrentOptionStyle(null);
                    flag = true;
                    this.filterOptions();
                }
                break;
        }
        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }
    onInputFocus(event) {
        this.filter = this.inputNode.value;
        // filter if the value is different now from what was there previously
        if (this.filter !== this.config.searchParams.search) {
            this.filterOptions();
        }
        this.setVisualFocusInput();
        this.option = null;
        this.setCurrentOptionStyle(null);
    }
    onInputBlur() {
        this.removeVisualFocusAll();
    }
    onBackgroundPointerUp(event) {
        if (!this.inputNode.contains(event.target) && !this.listboxNode.contains(event.target)) {
            this.inputHasVisualFocus = false;
            this.setCurrentOptionStyle(null);
            this.removeVisualFocusAll();
            setTimeout(this.close.bind(this, true), 300);
        }
    }
    onListboxPointerover() {
        this.hasHover = true;
    }
    onListboxPointerout() {
        this.hasHover = false;
        setTimeout(this.close.bind(this, false), 300);
    }
    onListboxClick(event) {
        var _a;
        // If this is an option. The listener is placed on the listbox (event.currentTarget). 
        const liElement = event.target.closest('li');
        if (liElement && event.currentTarget.contains(liElement)) {
            this.inputNode.value = (_a = liElement.textContent) !== null && _a !== void 0 ? _a : '';
            this.close(true);
            if (this.config.suggestions.onSelect) {
                this.config.suggestions.onSelect(event, liElement, this.inputNode.value);
            }
        }
    }
}
